import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { ReturnConsignmentsListStore } from 'zustand-store';
import { exchangeAndReturnService } from 'services';
import { useAuth } from 'hooks';
import { FormOption, ReturnEntries as ReturnEntriesType } from 'types';
import { ConfirmReturnModal } from 'pages/exchange-and-return/components';
import { useReturnDetails } from 'pages/exchange-and-return/hooks';
import { Button, Select, Text } from 'components';
import { formValidations } from 'helpers';
import { FontSize, FontWeight, outletRoutes, routes } from 'commons';
import { ProductForm } from './components';
import * as S from './styles';

interface Props {
  returnEntries: ReturnEntriesType;
}

const POST_OFFICE_CODE = '2';

export function ReturnEntries({ returnEntries }: Props) {
  const { mutate: mutateReturnConsignmentList } =
    ReturnConsignmentsListStore.useGetReturnConsignments();
  const { user } = useAuth();
  const { isAdmin } = user;

  const { setSelectedCard } = useReturnDetails();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function openModal() {
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
  }

  const { entries, optionsCollect, consignmentCode } = returnEntries;

  const hasOnlyOneItem = entries.length === 1;

  const options: FormOption[] = optionsCollect.map(({ title, code }) => {
    return {
      label: title,
      value: code,
    };
  });

  const initialValues = entries.map((returnEntry) => {
    const {
      entry: { entryNumber },
    } = returnEntry;

    return {
      isEnable: hasOnlyOneItem ? true : false,
      entry: entryNumber,
      reason: '',
      additionalReason: '',
      additionalInformation: '',
    };
  });

  const formValidation = Yup.object().shape({
    option: Yup.string().required('tipo de devolução'),
  });

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    setFieldValue,
    submitForm,
  } = useFormik({
    initialValues: {
      code: consignmentCode,
      entries: initialValues,
      option: undefined,
    },

    validateOnBlur: false,
    validateOnChange: false,

    validationSchema: formValidation,

    validate: (values) => {
      formValidations.validationSchema({
        schema: formValidation,
        values,
      });
    },

    onSubmit: (values, { setFieldError }): any => {
      const { entries, code, option } = values;

      const invalidEntry = entries.find(
        ({ isEnable, reason }) => isEnable && !reason
      );

      if (invalidEntry) {
        setFieldError(`entries[${invalidEntry.entry}].reason`, 'razão');

        return toast.warn('Campo razão necessário');
      }

      const submitEntries = entries
        .filter(({ isEnable }) => isEnable)
        .map(({ entry, reason, additionalReason, additionalInformation }) => {
          return {
            entry: entry ?? undefined,
            reason: reason || undefined,
            additionalReason: additionalReason || undefined,
            additionalInformation: additionalInformation || undefined,
          };
        });

      if (submitEntries.length === 0) {
        return toast.warn('Nenhum produto selecionado');
      }

      async function submitData() {
        setIsSubmitting(true);
        closeModal();

        const result = await exchangeAndReturnService.postCreateReturn({
          code,
          option,
          entries: submitEntries,
        });

        if (result) {
          const { exchangeAndReturn } = routes;
          const { details } = outletRoutes;

          setSelectedCard({
            type: 'return',
            consignmentCode: result.consignmentCode,
            protocol: result.protocol,
          });

          await mutateReturnConsignmentList();

          navigate(`${exchangeAndReturn.path}/${details.path}`);

          setIsSubmitting(false);
        }
      }

      if (showModal || option === POST_OFFICE_CODE) {
        submitData();
      } else {
        openModal();
      }
    },
  });

  if (!entries.length) {
    return (
      <S.TextWrapper>
        <Text weight={FontWeight.bold}>
          Nenhum produto disponível para devolução nesta remessa!
        </Text>
      </S.TextWrapper>
    );
  }

  const renderEntries = entries.map((returnEntry, index) => (
    <ProductForm
      key={index}
      name={`entries[${index}]`}
      errors={errors.entries?.[0]}
      values={values.entries[index]}
      handleChange={handleChange}
      returnEntry={returnEntry}
      setFieldValue={setFieldValue}
    />
  ));

  const enableButton = values.entries.some((entry) => entry.isEnable);

  return (
    <S.Form onSubmit={handleSubmit}>
      {showModal && (
        <ConfirmReturnModal
          closeModal={closeModal}
          submitForm={submitForm}
          isSubmitting={isSubmitting}
        />
      )}

      <S.SelectWrapper>
        <Text size={FontSize.font12}>
          O produto será devolvido na loja ou enviado pelo correio?
        </Text>

        <Select.Wrapper
          status={errors.option ? 'error' : 'default'}
          options={options}
          name="option"
          onChange={handleChange}
          disabled={isAdmin}
        >
          <Select.Input placeholder="Escolha o tipo de devolução" />

          <Select.StatusArrow />
        </Select.Wrapper>
      </S.SelectWrapper>

      {renderEntries}

      <S.ButtonsWrapper>
        <Button
          type="button"
          variant="outline-secondary"
          disabled={isSubmitting || isAdmin}
          onClick={() => {
            setSelectedCard(null);
            navigate(routes.exchangeAndReturn.path);
          }}
        >
          Cancelar
        </Button>

        <Button
          type="submit"
          disabled={!enableButton || isAdmin}
          isLoading={isSubmitting}
        >
          {values.option === POST_OFFICE_CODE
            ? 'Gerar código de postagem'
            : 'Confirmar devolução'}
        </Button>
      </S.ButtonsWrapper>
    </S.Form>
  );
}
